import React, { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  BarChart,
  Bar,
} from "recharts";
import {
  Card,
  Typography,
  Empty,
  Spin,
  Radio,
  Row,
  Col,
  Statistic,
  Space,
} from "antd";
import {
  CalendarOutlined,
  UserOutlined,
  ManOutlined,
  WomanOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Title, Text } = Typography;

const CampaignPostsEvolutionChart = ({
  invoices = [],
  users = [],
  selectedCampaign = null,
  loading = false,
}) => {
  const [viewMode, setViewMode] = useState("gender"); // 'timeline', 'gender', 'age'
  const [ageMode, setAgeMode] = useState("groups"); // 'groups', 'distribution'

  // Helper function to calculate age
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const birth = moment(birthDate);
    const today = moment();
    return today.diff(birth, "years");
  };

  // Helper function to get age group
  const getAgeGroup = (age) => {
    if (age < 18) return "<18";
    if (age < 25) return "18-24";
    if (age < 35) return "25-34";
    if (age < 45) return "35-44";
    if (age < 55) return "45-54";
    if (age < 65) return "55-64";
    return "65+";
  };

  const timelineData = useMemo(() => {
    if (!selectedCampaign || !invoices.length) return [];

    // Get campaign dates
    const startDate = moment(selectedCampaign.dateDebut);
    const endDate = moment(selectedCampaign.dateFin);
    const today = moment();

    // Filter posts for the selected campaign
    const campaignPosts = invoices.filter(
      (post) => post.selectedCampaignName === selectedCampaign.name
    );

    // Create an array of all days in the campaign period
    const days = [];
    let currentDate = startDate.clone();

    while (currentDate.isSameOrBefore(endDate, "day")) {
      days.push({
        date: currentDate.format("YYYY-MM-DD"),
        displayDate: currentDate.format("DD/MM"),
        fullDate: currentDate.clone(),
        posts: 0,
        cumulativePosts: 0,
        isToday: currentDate.isSame(today, "day"),
        isInPast: currentDate.isBefore(today, "day"),
      });
      currentDate.add(1, "day");
    }

    // Count posts per day
    campaignPosts.forEach((post) => {
      const postDate = moment(post.creationDate, "DD MMM YYYY [à] HH:mm");
      const dayIndex = days.findIndex((day) =>
        day.fullDate.isSame(postDate, "day")
      );

      if (dayIndex !== -1) {
        days[dayIndex].posts++;
      }
    });

    // Calculate cumulative posts
    let cumulative = 0;
    days.forEach((day, index) => {
      cumulative += day.posts;
      days[index].cumulativePosts = cumulative;
    });

    return days;
  }, [invoices, selectedCampaign]);

  const genderData = useMemo(() => {
    if (!selectedCampaign || !invoices.length) return [];

    const campaignPosts = invoices.filter(
      (post) => post.selectedCampaignName === selectedCampaign.name
    );

    const genderCounts = { Homme: 0, Femme: 0, "Non-spécifié": 0 };

    campaignPosts.forEach((post) => {
      const userId = post.userId?._id;
      const user = users.find((u) => u._id === userId);
      const gender = user?.gender || "Non-spécifié";

      if (gender === "Homme" || gender === "homme") {
        genderCounts.Homme++;
      } else if (gender === "Femme" || gender === "femme") {
        genderCounts.Femme++;
      } else {
        genderCounts["Non-spécifié"]++;
      }
    });

    return [
      { name: "Hommes", value: genderCounts.Homme, color: "#3B82F6" },
      { name: "Femmes", value: genderCounts.Femme, color: "#EC4899" },
      {
        name: "Non-spécifié",
        value: genderCounts["Non-spécifié"],
        color: "#6B7280",
      },
    ];
  }, [invoices, users, selectedCampaign]);

  const ageData = useMemo(() => {
    if (!selectedCampaign || !invoices.length) return [];

    const campaignPosts = invoices.filter(
      (post) => post.selectedCampaignName === selectedCampaign.name
    );

    if (ageMode === "groups") {
      const ageGroups = {
        "<18": 0,
        "18-24": 0,
        "25-34": 0,
        "35-44": 0,
        "45-54": 0,
        "55-64": 0,
        "65+": 0,
      };

      campaignPosts.forEach((post) => {
        const userId = post.userId?._id;
        const user = users.find((u) => u._id === userId);
        const age = calculateAge(user?.date);

        if (age !== null) {
          const ageGroup = getAgeGroup(age);
          ageGroups[ageGroup]++;
        }
      });

      return Object.entries(ageGroups).map(([group, count]) => ({
        name: group,
        value: count,
        color: "#10B981",
      }));
    } else {
      // Individual age distribution
      const ages = {};
      campaignPosts.forEach((post) => {
        const userId = post.userId?._id;
        const user = users.find((u) => u._id === userId);
        const age = calculateAge(user?.date);

        if (age !== null && age >= 16 && age <= 80) {
          ages[age] = (ages[age] || 0) + 1;
        }
      });

      return Object.entries(ages)
        .map(([age, count]) => ({
          age: parseInt(age),
          posts: count,
        }))
        .sort((a, b) => a.age - b.age);
    }
  }, [invoices, users, selectedCampaign, ageMode]);

  const stats = useMemo(() => {
    if (!selectedCampaign || !timelineData.length) return {};

    const totalPosts = timelineData.reduce((sum, day) => sum + day.posts, 0);
    const daysWithPosts = timelineData.filter((day) => day.posts > 0).length;
    const averagePostsPerDay =
      daysWithPosts > 0 ? (totalPosts / daysWithPosts).toFixed(1) : 0;
    const maxPostsInDay = Math.max(...timelineData.map((d) => d.posts), 0);
    const campaignDays = timelineData.length;
    const activeDays = daysWithPosts;

    return {
      totalPosts,
      averagePostsPerDay,
      maxPostsInDay,
      campaignDays,
      activeDays,
      participation: ((activeDays / campaignDays) * 100).toFixed(1),
    };
  }, [timelineData, selectedCampaign]);

  if (loading) {
    return (
      <Card style={{ marginTop: 24 }}>
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!selectedCampaign) {
    return (
      <Card style={{ marginTop: 24 }}>
        <Empty
          description="Sélectionnez une campagne pour voir l'évolution des posts"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  const renderTimelineChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={timelineData}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis
          dataKey="displayDate"
          tick={{ fontSize: 12 }}
          interval="preserveStartEnd"
        />
        <YAxis
          label={{
            value: "Nombre de posts",
            angle: -90,
            position: "insideLeft",
          }}
          tick={{ fontSize: 12 }}
        />
        <Tooltip
          formatter={(value, name) => [
            value,
            name === "posts" ? "Posts du jour" : "Posts cumulés",
          ]}
          labelFormatter={(label) => {
            const day = timelineData.find((d) => d.displayDate === label);
            return day ? day.fullDate.format("DD MMMM YYYY") : label;
          }}
        />
        {timelineData.findIndex((d) => d.isToday) !== -1 && (
          <ReferenceLine
            x={timelineData.find((d) => d.isToday)?.displayDate}
            stroke="#FF4D4F"
            strokeDasharray="5 5"
            label={{ value: "Aujourd'hui", position: "topLeft" }}
          />
        )}
        <Line
          type="monotone"
          dataKey="posts"
          stroke="#3B82F6"
          strokeWidth={3}
          dot={{ r: 5, fill: "#3B82F6" }}
          activeDot={{ r: 7, fill: "#1D4ED8" }}
        />
        <Line
          type="monotone"
          dataKey="cumulativePosts"
          stroke="#10B981"
          strokeWidth={2}
          strokeDasharray="5 5"
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderGenderChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={genderData}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
        <YAxis
          label={{
            value: "Nombre de posts",
            angle: -90,
            position: "insideLeft",
          }}
          tick={{ fontSize: 12 }}
        />
        <Tooltip formatter={(value) => [value, "Posts"]} />
        <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderAgeChart = () => {
    if (ageMode === "groups") {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={ageData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis
              label={{
                value: "Nombre de posts",
                angle: -90,
                position: "insideLeft",
              }}
              tick={{ fontSize: 12 }}
            />
            <Tooltip formatter={(value) => [value, "Posts"]} />
            <Bar dataKey="value" fill="#10B981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    } else {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={ageData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              dataKey="age"
              tick={{ fontSize: 12 }}
              label={{ value: "Âge", position: "insideBottom", offset: -5 }}
            />
            <YAxis
              label={{
                value: "Nombre de posts",
                angle: -90,
                position: "insideLeft",
              }}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(value) => [value, "Posts"]}
              labelFormatter={(age) => `Âge: ${age} ans`}
            />
            <Bar dataKey="posts" fill="#10B981" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }
  };

  return (
    <Card style={{ marginTop: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title
          level={4}
          style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}
        >
          <CalendarOutlined />
          Évolution du nombre de posts selon le temps de la campagne
        </Title>
        <Text type="secondary">{selectedCampaign.name}</Text>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Radio.Group
          value={viewMode}
          onChange={(e) => setViewMode(e.target.value)}
          style={{ marginBottom: 16 }}
        >
          {/* <Radio.Button value="timeline">Chronologie</Radio.Button> */}
          <Radio.Button value="gender">Par genre</Radio.Button>
          <Radio.Button value="age">Par âge</Radio.Button>
        </Radio.Group>

        {viewMode === "age" && (
          <Radio.Group
            value={ageMode}
            onChange={(e) => setAgeMode(e.target.value)}
            size="small"
          >
            <Radio.Button value="groups">Groupes d'âge</Radio.Button>
            <Radio.Button value="distribution">
              Distribution détaillée
            </Radio.Button>
          </Radio.Group>
        )}
      </div>

      {viewMode === "timeline" && (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Statistic
                title="Total Posts"
                value={stats.totalPosts}
                prefix={<UserOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Moyenne posts/jour"
                value={stats.averagePostsPerDay}
                precision={1}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Jours actifs"
                value={`${stats.activeDays}/${stats.campaignDays}`}
                suffix={`(${stats.participation}%)`}
              />
            </Col>
          </Row>
          {renderTimelineChart()}
        </>
      )}

      {viewMode === "gender" && (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Statistic
                title="Posts Hommes"
                value={genderData.find((g) => g.name === "Hommes")?.value || 0}
                prefix={<ManOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Posts Femmes"
                value={genderData.find((g) => g.name === "Femmes")?.value || 0}
                prefix={<WomanOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Non-spécifié"
                value={
                  genderData.find((g) => g.name === "Non-spécifié")?.value || 0
                }
                prefix={<UserOutlined />}
              />
            </Col>
          </Row>
          {renderGenderChart()}
        </>
      )}

      {viewMode === "age" && (
        <>
          <div style={{ marginBottom: 24 }}>
            <Statistic
              title="Total Posts analysés"
              value={ageData.reduce(
                (sum, item) => sum + (item.value || item.posts || 0),
                0
              )}
            />
          </div>
          {renderAgeChart()}
        </>
      )}
    </Card>
  );
};

export default CampaignPostsEvolutionChart;
